import { defineComponent as _defineComponent } from 'vue'
import { normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, watchEffect, computed, unref, watch } from 'vue';
import { get, omit } from 'lodash-es';
import { LoadingOutlined } from '@ant-design/icons-vue';
import { selectProps } from 'ant-design-vue/es/select';
import { Select } from 'ant-design-vue';
import type { PropType } from 'vue';
import { isFunction } from '@/utils/is';
import { useI18n } from '@/hooks/useI18n';
import { propTypes } from '@/utils/propTypes';

type OptionsItem = { label: string; value: string; disabled?: boolean };



export default /*@__PURE__*/_defineComponent({
  __name: 'ApiSelect',
  props: {
  ...selectProps(),
  value: [Array, Object, String, Number],
  numberToString: propTypes.bool,
  api: {
    type: Function as PropType<(arg?: Recordable) => Promise<any>>,
    default: null,
  },
  // api params
  params: {
    type: Object as PropType<Recordable>,
    default: () => ({}),
  },
  // support xxx.xxx.xx
  resultField: propTypes.string.def(''),
  labelField: propTypes.string.def('label'),
  valueField: propTypes.string.def('value'),
  immediate: propTypes.bool.def(true),
  alwaysLoad: propTypes.bool.def(false),
},
  emits: ['options-change', 'change'],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const options = ref<OptionsItem[]>([]);
const loading = ref(false);
const isFirstLoad = ref(true);
const emitData = ref<any[]>([]);
const { t } = useI18n();

const getProps = computed(() => props as Recordable);

// Embedded in the form, just use the hook binding to perform form verification

const getOptions = computed(() => {
  const { labelField, valueField, numberToString } = props;

  return unref(options).reduce((prev, next: Recordable) => {
    if (next) {
      const value = next[valueField];
      prev.push({
        ...omit(next, [labelField, valueField]),
        label: next[labelField],
        value: numberToString ? `${value}` : value,
      });
    }
    return prev;
  }, [] as OptionsItem[]);
});

watchEffect(() => {
  props.immediate && !props.alwaysLoad && fetch();
});

watch(
  () => props.params,
  () => {
    !unref(isFirstLoad) && fetch();
  },
  { deep: true },
);

async function fetch() {
  const api = props.api;
  if (!api || !isFunction(api)) return;
  options.value = [];
  try {
    loading.value = true;
    const res = await api(props.params);
    if (Array.isArray(res)) {
      options.value = res;
      emitChange();
      return;
    }
    if (props.resultField) {
      options.value = get(res, props.resultField) || [];
    }
    emitChange();
  } catch (error) {
    console.warn(error);
  } finally {
    loading.value = false;
  }
}

async function handleFetch(visible) {
  if (visible) {
    if (props.alwaysLoad) {
      await fetch();
    } else if (!props.immediate && unref(isFirstLoad)) {
      await fetch();
      isFirstLoad.value = false;
    }
  }
}

function emitChange() {
  emit('options-change', unref(getOptions));
}

function handleChange(_, ...args) {
  emitData.value = args;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Select), _mergeProps(getProps.value, {
    options: getOptions.value,
    onDropdownVisibleChange: handleFetch,
    onChange: handleChange
  }), _createSlots({ _: 2 }, [
    _renderList(Object.keys(_ctx.$slots), (item) => {
      return {
        name: item,
        fn: _withCtx((data) => [
          _renderSlot(_ctx.$slots, item, _normalizeProps(_guardReactiveProps(data || {})))
        ])
      }
    }),
    (loading.value)
      ? {
          name: "suffixIcon",
          fn: _withCtx(() => [
            _createVNode(_unref(LoadingOutlined), { spin: "" })
          ]),
          key: "0"
        }
      : undefined,
    (loading.value)
      ? {
          name: "notFoundContent",
          fn: _withCtx(() => [
            _createElementVNode("span", null, [
              _createVNode(_unref(LoadingOutlined), {
                spin: "",
                class: "mr-1"
              }),
              _createTextVNode(" " + _toDisplayString(_unref(t)('component.form.apiSelectNotFound')), 1)
            ])
          ]),
          key: "1"
        }
      : undefined
  ]), 1040, ["options"]))
}
}

})